import { Badge, Menu, Space, theme, Tooltip } from "antd";
import { ItemType } from "antd/lib/menu/interface";
import { useWindowSize } from "hooks/use.window.size";
import Link from "next/link";
import { useRouter } from "next/navigation";
import React, {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "store";
import { toggle } from "store/menu";
import styled from "styled-components";

const MainMenu = styled(Menu)`
  background: none;
  border: none;
  max-width: 360px;
  border-inline-end: none !important;
  padding-left: 12px;

  .geStarted {
    @media (max-width: 1100px) {
      display: none;
    }
  }

  .ant-menu-item-selected {
    width: 100% !important;
  }

  .ant-menu-sub.ant-menu-inline {
    overflow: hidden;
  }

  .ant-menu-sub.ant-menu-inline > .ant-menu-item {
    margin: 0;
  }

  .ant-menu {
    border-radius: 8px !important;
  }

  .ant-menu-item::after {
    border-right: none;
  }

  .ant-menu-item.ant-menu-item-only-child {
    box-sizing: content-box;
    inline-size: min-content;
  }

  .ant-menu-item {
    padding-right: 0;
    width: 100%;

    .ant-btn-primary {
      color: #fff;
    }

    .ant-btn path {
      stroke: #fff;
    }
  }
`;

const MenuGroup = styled.div`
  margin-top: 64px;
  text-transform: uppercase;
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 1px;
`;

interface Props {
  selected?: string;
  availableMenu: MenuEntry[];
}

export interface SubmenuEntry {
  beta?: boolean;
  disabled?: boolean;
  fallback?: string;
  label?: ReactNode;
  hide?: boolean;
  href?: string;
  icon?: React.ReactNode;
  key: string;
  mode?: string;
  pro?: boolean;
  submenu?: SubmenuEntry[];
  style?: CSSProperties;
  target?: string;
  text?: string;
  type?: string;
  tooltip?: string;

  menu?: boolean;
}

export interface MenuEntry extends SubmenuEntry {
  submenu?: SubmenuEntry[];
}

export const MENU_TOGGLE_WIDTH = 1024;

export const BaseMenu: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const canToggle = width < MENU_TOGGLE_WIDTH;
  const router = useRouter();
  const dispatch = useAppDispatch();
  const {
    token: { colorBgLayout },
  } = theme.useToken();

  const getLabel = useCallback(
    (
      item: SubmenuEntry,
      beta: boolean,
      tooltip: string,
      pro: boolean,
    ): ReactNode => {
      if (item.label) {
        if (tooltip) {
          return <Tooltip title={tooltip}>{item.label}</Tooltip>;
        }

        return item.label;
      }

      let text: ReactNode = t(item.text, item.fallback);

      if (tooltip) {
        text = <Tooltip title={tooltip}>{t(item.text, item.fallback)}</Tooltip>;
      }

      if (item.type === "group") {
        return <MenuGroup>{text}</MenuGroup>;
      }

      return (
        <Space>
          {text}
          {beta && (
            <Badge
              count={t("menu-beta", "BETA")}
              size={"small"}
              style={{
                backgroundColor: "#EB6435",
                boxShadow: "none",
              }}
            />
          )}
          {pro && (
            <Badge
              count={t("menu-pro", "PRO")}
              size={"small"}
              style={{
                backgroundColor: "#EB6435",
              }}
            />
          )}
        </Space>
      );
    },
    [],
  );

  const getMenu = useCallback((): ItemType[] => {
    const filter = (entries: MenuEntry[]): ItemType[] => {
      return entries
        .filter((entry) => !entry.hide && !(entry.menu === false))
        .map(({ hide, beta, pro, tooltip, href, target, ...entry }) => {
          let label = getLabel(entry, beta, tooltip, pro);

          if (href) {
            label = (
              <Link href={href} target={target}>
                {label}
              </Link>
            );
          }

          return {
            ...entry,
            children: entry.submenu ? filter(entry.submenu) : undefined,
            label,
          } as ItemType;
        });
    };
    return filter(props.availableMenu);
  }, [props.availableMenu]);

  const [menu, setMenu] = useState<ItemType[]>(getMenu());

  useEffect(() => {
    setMenu(getMenu());
  }, [props.availableMenu]);

  const [openedKeys, setOpenedKeys] = useState<string[]>(() => {
    if (!props.selected) {
      return [];
    }

    const prepare = [];
    props.availableMenu.forEach((entry) => {
      if (entry.submenu) {
        prepare.push(...entry.submenu);
      }
      prepare.push(entry);
    });

    return prepare
      .filter((entry) => {
        return (
          entry.submenu?.some((subentry) => subentry.key === props.selected) ||
          entry.key == props.selected
        );
      })
      .map((entry) => entry.key)
      .filter((e) => !!e);
  });

  const itemClick = useCallback((element) => {
    const { href } = element.item.props;
    if (href) {
      router.push(href);
    }
    if (!canToggle) {
      dispatch(toggle(true));
    }
  }, []);

  return (
    <Space
      orientation={"vertical"}
      size={16}
      style={{ paddingRight: "10px", paddingTop: "20px", width: "100%" }}
    >
      {props.availableMenu
        .filter((m) => m.hide !== true && m.menu === false)
        .map((m) => {
          return (
            <div
              key={m.key}
              style={{
                marginLeft: 24,
                marginRight: width < 1024 ? 24 : undefined,
                ...m.style,
              }}
            >
              {m.label}
            </div>
          );
        })}
      <MainMenu
        onOpenChange={(opened: string[]) => {
          setOpenedKeys(opened);
        }}
        openKeys={openedKeys}
        selectedKeys={[props.selected]}
        mode={"inline"}
        items={menu}
        onClick={itemClick}
        inlineIndent={12}
      />
    </Space>
  );
};
